// ✅ src/components/admin/AdminPaymentsScreen.js
// ✅ UPDATED: shows referrer withdrawal details + writes referral history when reward is paid

import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../lib/firebase";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  runTransaction,
  increment,
  where,
  getDocs,
} from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}

function toTime(ts) {
  try {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "";
  }
}

function money(n, cur = "GHS") {
  const num = Number(n ?? 0);
  if (Number.isNaN(num)) return `${cur} 0`;
  return `${cur} ${num.toLocaleString()}`;
}

export default function AdminPaymentsScreen({ goBack, role }) {
  const roleNorm = safe(role).toUpperCase();
  const isAdmin = roleNorm === "ADMIN" || roleNorm === "WORKER";

  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [qText, setQText] = useState("");
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  // ✅ NEW: referrer withdrawal requests
  const [refWithdrawals, setRefWithdrawals] = useState([]);
  const [refWLoading, setRefWLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setMsg("");

    const q = query(collection(db, "payments"), orderBy("createdAt", "desc"), limit(200));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRows(data);
        setLoading(false);

        if (selected?.id) {
          const latest = data.find((x) => x.id === selected.id);
          if (latest) setSelected(latest);
        }
      },
      (err) => {
        console.log("Payments listener error:", err);
        setRows([]);
        setLoading(false);
        setMsg("❌ Can't read /payments (rules/index?)");
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ when selected changes, load withdrawal requests for referrer
  useEffect(() => {
    async function loadRefWithdrawals() {
      const refUid = safe(selected?.referrerUid);
      if (!refUid) {
        setRefWithdrawals([]);
        return;
      }

      setRefWLoading(true);
      try {
        const wQ = query(
          collection(db, "withdrawals"),
          where("uid", "==", refUid),
          where("status", "==", "REQUESTED"),
          orderBy("createdAt", "desc"),
          limit(10)
        );
        const wSnap = await getDocs(wQ);
        setRefWithdrawals(wSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.log("loadRefWithdrawals error:", e);
        setRefWithdrawals([]);
      } finally {
        setRefWLoading(false);
      }
    }

    loadRefWithdrawals();
  }, [selected?.referrerUid]); // eslint-disable-line

  const filtered = useMemo(() => {
    const t = safe(qText).toLowerCase();
    if (!t) return rows;
    return rows.filter((p) => {
      const a = safe(p.uid).toLowerCase();
      const b = safe(p.storeName).toLowerCase();
      const c = safe(p.storeSlug).toLowerCase();
      const d = safe(p.method).toLowerCase();
      const e = safe(p.referrerUid).toLowerCase();
      return a.includes(t) || b.includes(t) || c.includes(t) || d.includes(t) || e.includes(t);
    });
  }, [rows, qText]);

  const pendingCount = useMemo(() => {
    return filtered.filter((x) => safe(x.status).toUpperCase() === "PENDING").length;
  }, [filtered]);

  async function patchPayment(paymentId, patch) {
    setMsg("");
    try {
      await updateDoc(doc(db, "payments", paymentId), {
        ...patch,
        updatedAt: serverTimestamp(),
      });
      setMsg("✅ Updated");
    } catch (e) {
      console.log(e);
      setMsg("❌ Failed (rules?)");
    }
  }

  async function confirmPayment(p) {
    const ok = window.confirm("Confirm this payment?");
    if (!ok) return;

    const sellerUid = safe(p.uid);
    if (!sellerUid) {
      setMsg("❌ Payment doc missing uid");
      return;
    }

    try {
      // 1) payment -> CONFIRMED
      await patchPayment(p.id, {
        status: "CONFIRMED",
        confirmedAt: serverTimestamp(),
        confirmedBy: roleNorm || "ADMIN",
      });

      // 2) store doc -> activate subscription
      await updateDoc(doc(db, "stores", sellerUid), {
        subscriptionStatus: "ACTIVE",
        subscriptionPaidAt: serverTimestamp(),
        subscriptionLastPaymentId: p.id,
        updatedAt: serverTimestamp(),
      });

      // ✅ OPTIONAL: if referred, mark referral as QUALIFIED (history)
      const referrerUid = safe(p.referrerUid);
      if (referrerUid) {
        const referralId = `${referrerUid}_${sellerUid}`;
        await updateDoc(
          doc(db, "referrals", referralId),
          {
            referrerUid,
            sellerUid,
            sellerStoreName: safe(p.storeName),
            currency: safe(p.currency) || "GHS",
            rewardAmount: Number(p.referralRewardAmount ?? 7),
            status: "QUALIFIED",
            paymentId: p.id,
            qualifiedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          }
        ).catch(async () => {
          // if doc doesn't exist, setDoc style using updateDoc fails, so do a set via transaction below in reward step
        });
      }

      setMsg("✅ Payment confirmed + store activated");
    } catch (e) {
      console.log(e);
      setMsg("❌ Confirm failed (rules?)");
    }
  }

  async function rejectPayment(p) {
    const reason = prompt("Reject reason (optional):") || "";
    const ok = window.confirm("Reject this payment?");
    if (!ok) return;

    await patchPayment(p.id, {
      status: "REJECTED",
      rejectedAt: serverTimestamp(),
      rejectedBy: roleNorm || "ADMIN",
      rejectReason: safe(reason),
    });
  }

  // ✅ Referral reward payout + write referral history
  async function payReferralReward(p) {
    setMsg("");

    const sellerUid = safe(p.uid);
    const referrerUid = safe(p.referrerUid);
    const rewardStatus = safe(p.referralRewardStatus || "UNPAID").toUpperCase();
    const rewardAmount = Number(p.referralRewardAmount ?? 7);
    const currency = safe(p.currency) || "GHS";

    if (!sellerUid) return setMsg("❌ Payment missing seller uid");
    if (!referrerUid) return setMsg("❌ No referrerUid on this payment");
    if (rewardStatus === "PAID") return setMsg("✅ Reward already PAID");
    if (!rewardAmount || Number.isNaN(rewardAmount) || rewardAmount <= 0)
      return setMsg("❌ Invalid referralRewardAmount");

    const ok = window.confirm(`Pay referral reward ${money(rewardAmount, currency)} to referrer?`);
    if (!ok) return;

    const payRef = doc(db, "payments", p.id);
    const walletRef = doc(db, "wallets", referrerUid);
    const txRef = doc(collection(db, "wallets", referrerUid, "transactions")); // auto id

    // ✅ referral history doc (stable id per referrer+seller)
    const referralId = `${referrerUid}_${sellerUid}`;
    const referralRef = doc(db, "referrals", referralId);

    try {
      await runTransaction(db, async (tx) => {
        const paySnap = await tx.get(payRef);
        if (!paySnap.exists()) throw new Error("payment_missing");

        const pay = paySnap.data() || {};
        const currentStatus = safe(pay.referralRewardStatus || "UNPAID").toUpperCase();
        const currentRef = safe(pay.referrerUid);

        if (!currentRef) throw new Error("no_referrer");
        if (currentStatus === "PAID") throw new Error("already_paid");

        // 1) Mark reward paid on payment doc
        tx.update(payRef, {
          referralRewardStatus: "PAID",
          referralRewardPaidAt: serverTimestamp(),
          referralRewardPaidBy: roleNorm || "ADMIN",
          referralRewardAmount: rewardAmount,
          updatedAt: serverTimestamp(),
        });

        // 2) Credit referrer wallet
        tx.set(
          walletRef,
          {
            currency,
            updatedAt: serverTimestamp(),
            balance: increment(rewardAmount),
            totalEarned: increment(rewardAmount),
          },
          { merge: true }
        );

        // 3) Add wallet transaction
        tx.set(txRef, {
          type: "REFERRAL_REWARD",
          direction: "CREDIT",
          amount: rewardAmount,
          currency,
          status: "PAID",
          refId: p.id,
          meta: {
            sellerUid,
            paymentId: p.id,
            storeName: safe(p.storeName),
          },
          createdAt: serverTimestamp(),
        });

        // ✅ 4) Write referral history (this is what shows on Earn screen)
        tx.set(
          referralRef,
          {
            referrerUid,
            sellerUid,
            sellerStoreName: safe(p.storeName),
            paymentId: p.id,
            currency,
            rewardAmount,
            status: "REWARDED",
            qualifiedAt: pay.confirmedAt || null,
            rewardedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );
      });

      setMsg("✅ Referral reward PAID + wallet credited + referral history saved");
    } catch (e) {
      console.log(e);
      if (String(e?.message || "").includes("already_paid")) setMsg("✅ Reward already PAID");
      else setMsg("❌ Reward payment failed (rules?)");
    }
  }

  async function rejectReferralReward(p) {
    setMsg("");
    const referrerUid = safe(p.referrerUid);
    if (!referrerUid) return setMsg("❌ No referrerUid on this payment");

    const reason = prompt("Reject reward reason (optional):") || "";
    const ok = window.confirm("Reject referral reward for this payment?");
    if (!ok) return;

    await patchPayment(p.id, {
      referralRewardStatus: "REJECTED",
      referralRewardRejectedAt: serverTimestamp(),
      referralRewardRejectedBy: roleNorm || "ADMIN",
      referralRewardRejectReason: safe(reason),
    });

    // also mark referral history if you want
    try {
      const sellerUid = safe(p.uid);
      const referralId = `${referrerUid}_${sellerUid}`;
      await updateDoc(doc(db, "referrals", referralId), {
        status: "CANCELLED",
        updatedAt: serverTimestamp(),
        cancelledAt: serverTimestamp(),
        cancelledReason: safe(reason),
      });
    } catch {}

    setMsg("✅ Reward rejected");
  }

  if (!isAdmin) {
    return (
      <div style={S.page}>
        <div style={S.top}>
          <button style={S.back} onClick={goBack}>
            ←
          </button>
          <div>
            <div style={S.h1}>Payments</div>
            <div style={S.sub}>Admins/Workers only</div>
          </div>
        </div>
        <div style={S.card}>
          Only <b>ADMIN</b> or <b>WORKER</b> can open Payments.
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.top}>
        <button style={S.back} onClick={goBack}>
          ←
        </button>
        <div>
          <div style={S.h1}>Payments</div>
          <div style={S.sub}>Review proofs and confirm/reject seller payments.</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
          <span style={S.kpi}>Pending: {pendingCount}</span>
          <span style={S.badge}>{roleNorm}</span>
        </div>
      </div>

      <div style={S.wrap}>
        <div style={S.row}>
          <input
            style={S.input}
            value={qText}
            onChange={(e) => setQText(e.target.value)}
            placeholder="Search uid / store / method / referrer..."
          />
          <div style={S.kpi}>{filtered.length} payments</div>
        </div>

        {msg ? <div style={S.msg}>{msg}</div> : null}

        <div style={S.grid}>
          {/* LIST */}
          <div style={S.list}>
            {loading ? (
              <div style={S.card}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div style={S.card}>No payments in /payments.</div>
            ) : (
              filtered.map((p) => {
                const status = safe(p.status || "PENDING").toUpperCase();
                const isPending = status === "PENDING";
                const hasRef = Boolean(safe(p.referrerUid));
                const rStatus = safe(p.referralRewardStatus || "UNPAID").toUpperCase();

                return (
                  <button
                    key={p.id}
                    type="button"
                    style={{
                      ...S.item,
                      ...(selected?.id === p.id ? S.itemOn : null),
                    }}
                    onClick={() => setSelected(p)}
                  >
                    <div style={S.itemTop}>
                      <div style={S.itemTitle}>
                        {safe(p.storeName) || "Store payment"}
                        {isPending ? <span style={S.pillWarn}>PENDING</span> : null}
                        {status === "CONFIRMED" ? <span style={S.pillGood}>CONFIRMED</span> : null}
                        {status === "REJECTED" ? <span style={S.pillBad}>REJECTED</span> : null}

                        {hasRef ? (
                          <span
                            style={
                              rStatus === "PAID"
                                ? S.pillGood
                                : rStatus === "REJECTED"
                                ? S.pillBad
                                : S.pillWarn
                            }
                          >
                            REF: {rStatus}
                          </span>
                        ) : null}
                      </div>
                      <div style={S.itemAmt}>{money(p.amount, safe(p.currency) || "GHS")}</div>
                    </div>
                    <div style={S.itemSub}>
                      uid: {safe(p.uid).slice(0, 10)}… • {safe(p.method || "MOMO")} • {toTime(p.createdAt) || "—"}
                    </div>
                    {hasRef ? (
                      <div style={{ marginTop: 6, fontSize: 12, fontWeight: 850, color: "rgba(15,23,42,0.6)" }}>
                        referrer: {safe(p.referrerUid).slice(0, 10)}…
                      </div>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>

          {/* PANEL */}
          <div style={S.panel}>
            {!selected ? (
              <div style={S.card}>Pick a payment to review.</div>
            ) : (
              <div>
                <div style={S.panelTitle}>Payment details</div>

                <div style={S.pLine}>
                  Payment ID: <span style={S.code}>{selected.id}</span>
                </div>
                <div style={S.pLine}>
                  Seller UID: <span style={S.code}>{safe(selected.uid) || "—"}</span>
                </div>

                <div style={S.pLine}>
                  Store: <b>{safe(selected.storeName) || "—"}</b>{" "}
                  {safe(selected.storeSlug) ? <span style={S.code}>({safe(selected.storeSlug)})</span> : null}
                </div>

                <div style={S.pLine}>
                  Amount: <b>{money(selected.amount, safe(selected.currency) || "GHS")}</b>
                </div>
                <div style={S.pLine}>
                  Method: <b>{safe(selected.method || "—")}</b>
                </div>
                <div style={S.pLine}>
                  Status: <b>{safe(selected.status || "PENDING").toUpperCase()}</b>
                </div>
                <div style={S.pLine}>
                  Created: <b>{toTime(selected.createdAt) || "—"}</b>
                </div>

                {safe(selected.proofUrl) ? (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8 }}>Proof</div>
                    <a href={safe(selected.proofUrl)} target="_blank" rel="noreferrer" style={S.link}>
                      Open proof in new tab →
                    </a>
                    <div style={{ height: 10 }} />
                    <img
                      src={safe(selected.proofUrl)}
                      alt="proof"
                      style={S.proof}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                ) : (
                  <div style={S.note}>No proofUrl on this payment doc.</div>
                )}

                <div style={S.actions}>
                  <button style={S.btnGood} onClick={() => confirmPayment(selected)}>
                    Confirm payment
                  </button>
                  <button style={S.btnBad} onClick={() => rejectPayment(selected)}>
                    Reject
                  </button>
                </div>

                {/* ✅ Referral reward section */}
                {safe(selected.referrerUid) ? (
                  <div style={S.note}>
                    <div style={{ fontWeight: 950, marginBottom: 8 }}>Referral reward</div>
                    Referrer UID: <code>{safe(selected.referrerUid)}</code>
                    <br />
                    Reward amount:{" "}
                    <b>{money(Number(selected.referralRewardAmount ?? 7), safe(selected.currency) || "GHS")}</b>
                    <br />
                    Reward status: <b>{safe(selected.referralRewardStatus || "UNPAID").toUpperCase()}</b>

                    {/* ✅ show withdrawal details */}
                    <div style={{ height: 10 }} />
                    <div style={{ fontWeight: 950, marginBottom: 6 }}>Referrer withdrawal requests</div>
                    {refWLoading ? (
                      <div style={{ fontSize: 12, fontWeight: 800 }}>Loading withdrawals…</div>
                    ) : refWithdrawals.length === 0 ? (
                      <div style={{ fontSize: 12, fontWeight: 800 }}>
                        No REQUESTED withdrawals yet from this referrer.
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: 8, marginTop: 6 }}>
                        {refWithdrawals.map((w) => (
                          <div key={w.id} style={{ padding: 10, borderRadius: 14, border: "1px solid rgba(15,23,42,0.10)", background: "white" }}>
                            <div style={{ fontWeight: 950, fontSize: 12 }}>
                              {money(w.amount, safe(w.currency) || "GHS")} • {safe(w.status)}
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 850, color: "rgba(15,23,42,0.65)", marginTop: 4 }}>
                              {safe(w.momoNetwork)} • {safe(w.momoNumber)} • {safe(w.momoName)}
                              <br />
                              {toTime(w.createdAt)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ height: 10 }} />
                    <div style={S.actions}>
                      <button style={S.btnGood} onClick={() => payReferralReward(selected)}>
                        Pay referrer (Reward)
                      </button>
                      <button style={S.btnBad} onClick={() => rejectReferralReward(selected)}>
                        Reject reward
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={S.note}>
                    No referral on this payment. (Add <code>referrerUid</code> to the payment doc if referred.)
                  </div>
                )}

                <div style={S.note}>
                  Tip: This screen updates <code>payments/{`{id}`}.status</code> and sets{" "}
                  <code>stores/{`{uid}`}.subscriptionStatus = ACTIVE</code>. Paying referral reward writes{" "}
                  <code>referrals/{`{referrerUid}_{sellerUid}`}</code> + credits wallet.
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={S.note2}>
          Make sure Firestore rules allow ADMIN/WORKER to read/update <code>/payments</code>, update <code>/stores</code>,
          write <code>/referrals</code>, read <code>/withdrawals</code>, and credit <code>/wallets</code>.
        </div>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    padding: 14,
    background: "linear-gradient(180deg,#f7f8ff 0%,#eef2ff 45%,#f8fafc 100%)",
  },
  top: {
    maxWidth: 1100,
    margin: "0 auto 14px",
    display: "flex",
    gap: 12,
    alignItems: "center",
  },
  back: {
    width: 42,
    height: 42,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "white",
    cursor: "pointer",
    fontWeight: 900,
  },
  h1: { fontSize: 18, fontWeight: 950, color: "#0f172a" },
  sub: { marginTop: 2, fontSize: 12, fontWeight: 800, color: "rgba(15,23,42,0.60)" },
  wrap: { maxWidth: 1100, margin: "0 auto" },
  row: { display: "flex", gap: 10, alignItems: "center", marginBottom: 12 },
  input: {
    flex: 1,
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.12)",
    outline: "none",
    fontWeight: 800,
    background: "rgba(255,255,255,0.92)",
  },
  badge: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.9)",
    fontSize: 12,
    fontWeight: 900,
    color: "#0f172a",
  },
  kpi: {
    padding: "10px 12px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.85)",
    fontSize: 12,
    fontWeight: 950,
  },
  msg: {
    marginBottom: 12,
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "rgba(255,255,255,0.75)",
    fontSize: 12,
    fontWeight: 900,
  },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  list: { display: "grid", gap: 10 },
  item: {
    textAlign: "left",
    padding: 12,
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.92)",
    cursor: "pointer",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.04)",
  },
  itemOn: {
    border: "1px solid rgba(99,102,241,0.25)",
    background: "rgba(99,102,241,0.08)",
  },
  itemTop: { display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" },
  itemTitle: { display: "flex", gap: 8, alignItems: "center", fontWeight: 950, color: "#0f172a", flexWrap: "wrap" },
  itemAmt: { fontWeight: 950, color: "#0f172a", whiteSpace: "nowrap" },
  itemSub: { marginTop: 6, fontSize: 12, fontWeight: 800, color: "rgba(15,23,42,0.65)" },
  pillWarn: {
    fontSize: 11,
    fontWeight: 950,
    padding: "4px 8px",
    borderRadius: 999,
    border: "1px solid rgba(245,158,11,0.22)",
    background: "rgba(245,158,11,0.12)",
  },
  pillGood: {
    fontSize: 11,
    fontWeight: 950,
    padding: "4px 8px",
    borderRadius: 999,
    border: "1px solid rgba(34,197,94,0.20)",
    background: "rgba(34,197,94,0.12)",
  },
  pillBad: {
    fontSize: 11,
    fontWeight: 950,
    padding: "4px 8px",
    borderRadius: 999,
    border: "1px solid rgba(239,68,68,0.22)",
    background: "rgba(239,68,68,0.10)",
  },
  panel: {
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.92)",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
    padding: 14,
  },
  panelTitle: { fontSize: 14, fontWeight: 950, color: "#0f172a", marginBottom: 10 },
  pLine: { fontSize: 12, fontWeight: 850, color: "rgba(15,23,42,0.72)", marginTop: 8 },
  code: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
  actions: { display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" },
  btnGood: {
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(34,197,94,0.20)",
    background: "rgba(34,197,94,0.12)",
    cursor: "pointer",
    fontWeight: 950,
  },
  btnBad: {
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(239,68,68,0.20)",
    background: "rgba(239,68,68,0.10)",
    cursor: "pointer",
    fontWeight: 950,
  },
  card: {
    padding: 14,
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.92)",
  },
  note: {
    marginTop: 12,
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "rgba(248,250,252,0.75)",
    fontSize: 12,
    fontWeight: 800,
    color: "rgba(15,23,42,0.72)",
  },
  note2: {
    marginTop: 12,
    padding: 10,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "rgba(255,255,255,0.75)",
    fontSize: 12,
    fontWeight: 800,
    color: "rgba(15,23,42,0.72)",
  },
  proof: {
    width: "100%",
    maxHeight: 360,
    objectFit: "contain",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "white",
  },
  link: {
    display: "inline-block",
    fontSize: 12,
    fontWeight: 900,
    color: "#0f172a",
    textDecoration: "underline",
  },
};
